import React, { FC } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { DEFAULT_LOCALE, normalizeLocale } from '@rover/constants/src/i18n/locale';
import Section from '@rover/kibble/patterns/Section';
import useIsCatInAFlat from '@rover/react-lib/src/hooks/useIsCatInAFlat';
import LandingReviewsCarousel from '@rover/react-lib/src/pages/landing/components/LandingReviewsCarousel';
import LandingSearchResultsCarousel from '@rover/react-lib/src/pages/landing/components/LandingSearchResultsCarousel';
import QuickFact, {
  QuickFactHighlight,
} from '@rover/react-lib/src/pages/landing/components/QuickFact';
import { getSearchFiltersFromSeoLocationDetails } from '@rover/react-lib/src/pages/landing/utilities';
import useFacts from '@rover/react-lib/src/pages/landing/utilities/useFacts';
import AppStoreCallout from '@rover/react-lib/src/pages/shared/AppStoreCallout/AppStoreCallout';
import getSearchUrl from '@rover/react-lib/src/utils/getSearchUrl';
import { PetType } from '@rover/types/src/Pet';

import SeoHowBrandWorks from '../../components/SeoHowBrandWorks';
import SeoLocationFaqList from '../../components/SeoLocationFaqList';
import SeoLocationFooterLinks from '../../components/SeoLocationFooterLinks';
import SeoLocationHero from '../../components/SeoLocationHero';
import SeoLocationPageWrapper from '../../components/SeoLocationPageWrapper';
import SeoLocationRoverProtect from '../../components/SeoLocationRoverProtect';
import SeoLocationText from '../../components/SeoLocationText';
import ValuePropFilterOptions from '../../components/ValuePropFilterOptions';
import WhyChooseBrand from '../../components/WhyChooseBrand';
import { PageSectionId, useSectionRef } from '../../hooks/useSectionRef';
import { ciafQuickFactDoodle, quickFactBackgroundDoodle } from '../../images';
import { SeoLocationPageProps } from '../../types';
import onValuePropFilterOptionsCtaClick from '../../utils/onValuePropFilterOptionsCtaClick';

const SeoLocationPageTemplate: FC<SeoLocationPageProps> = ({
  seoLocation,
  countryConfig,
  frontendConfig,
  reviews,
  serviceTypes,
  topNeighborhoodServices,
  searchResponse,
}) => {
  const { t } = useLingui();
  const { linkedLocations, details } = seoLocation;
  const { language } = frontendConfig.settings;
  const locale = normalizeLocale(language) ?? DEFAULT_LOCALE;
  const initialSearchFilters = getSearchFiltersFromSeoLocationDetails(seoLocation);
  const reviewsSectionRef = useSectionRef(PageSectionId.ReviewsCarousel);
  const isCatInAFlat = useIsCatInAFlat();
  const quickFactSticker = isCatInAFlat ? ciafQuickFactDoodle : quickFactBackgroundDoodle;
  const outerQuickFactAlignment = isCatInAFlat ? 'left' : 'right';
  const {
    cityName,
    breadcrumbData,
    relatedServices,
    serviceTypeSlug,
    internalServiceType,
    petType,
    h2,
    facts,
    name: locationName,
  } = details;

  const {
    totalCustomersNum,
    medianListPricePerUnitLocalCurrencyNum,
    avgRepeatCustomerAmongSittersWithRepeatNum,
  } = useFacts(facts);
  const { titleCasedServiceKeyword, sentenceCasedServiceKeyword } = details;

  // The price unit varies per service type and comes pre-localized from the backend
  // (e.g. "walk", "night", "visit", "day"), so we interpolate it into a single sentence.
  const renderMedianCostFact = (formattedAmount: string): JSX.Element => (
    <Trans>
      The median cost per {internalServiceType.alternativeRateUnitTranslated} for{' '}
      {sentenceCasedServiceKeyword} in {locationName} is{' '}
      <QuickFactHighlight>{formattedAmount}</QuickFactHighlight>
    </Trans>
  );

  return (
    <SeoLocationPageWrapper seoLocation={seoLocation} countryConfig={countryConfig}>
      <Section>
        <SeoLocationHero
          subheading={h2}
          petType={petType}
          internalServiceTypeSlug={internalServiceType.slug}
          serviceTypeName={titleCasedServiceKeyword}
          locationName={locationName}
          locale={locale}
          serviceTypes={serviceTypes}
          internalServiceType={internalServiceType}
          initialSearchFilters={initialSearchFilters}
          // Cat in a Flat has a low TrustPilot rating, so product chose to hide the widget there.
          showTrustpilot={!isCatInAFlat}
        />
      </Section>

      {totalCustomersNum &&
        (() => {
          const { asNumber: ownerCount } = totalCustomersNum;
          return (
            <QuickFact
              stickerImageSrc={quickFactSticker}
              stickerAlignment={outerQuickFactAlignment}
            >
              <Trans>
                Over <QuickFactHighlight>{ownerCount}</QuickFactHighlight> pet parents in{' '}
                {locationName} have booked {sentenceCasedServiceKeyword}
              </Trans>
            </QuickFact>
          );
        })()}

      <Section my={['16x', '12x']}>
        <LandingSearchResultsCarousel
          searchResponse={searchResponse}
          searchFilters={initialSearchFilters}
          locale={locale}
          heading={t`${locationName}, Find top rated ${sentenceCasedServiceKeyword}`}
          headingVariant="heading"
          headingSize="400"
          serviceTypeSlug={serviceTypeSlug}
          countryConfig={countryConfig}
        />
      </Section>

      {medianListPricePerUnitLocalCurrencyNum &&
        (() => {
          const { asCurrency: formattedAmount } = medianListPricePerUnitLocalCurrencyNum;
          return (
            <QuickFact themeColor="pink" stickerImageSrc={quickFactSticker}>
              {renderMedianCostFact(formattedAmount)}
            </QuickFact>
          );
        })()}
      <LandingReviewsCarousel
        htmlRef={reviewsSectionRef}
        reviews={reviews}
        petType={(petType as PetType) ?? undefined}
        locationName={locationName}
        internalServiceTypeSlug={internalServiceType.slug}
        getCardHref={(review) => review.providerProfileUrl}
        cardHrefLabel={t`View Profile`}
        ctaHref={getSearchUrl(initialSearchFilters, locale)}
        my={['16x', '12x']}
      />
      <SeoLocationRoverProtect />
      <WhyChooseBrand />
      <SeoHowBrandWorks
        serviceTypeName={sentenceCasedServiceKeyword}
        providerNounPlural={internalServiceType.providerNounPlural}
        petType={(petType as PetType) ?? undefined}
      />
      <ValuePropFilterOptions
        my="12x"
        locationName={locationName}
        locale={locale}
        searchFilters={initialSearchFilters}
        internalServiceTypeSlug={internalServiceType.slug}
        petType={petType}
        serviceTypeName={sentenceCasedServiceKeyword}
        onPress={onValuePropFilterOptionsCtaClick(serviceTypeSlug)}
      />

      {avgRepeatCustomerAmongSittersWithRepeatNum &&
        (() => {
          const { asNumber: clientCount } = avgRepeatCustomerAmongSittersWithRepeatNum;
          return (
            <QuickFact
              themeColor="cyan"
              stickerImageSrc={quickFactSticker}
              stickerAlignment={outerQuickFactAlignment}
            >
              <Trans>
                Sitters in {locationName} average{' '}
                <QuickFactHighlight>{clientCount}</QuickFactHighlight> repeat{' '}
                {sentenceCasedServiceKeyword} clients
              </Trans>
            </QuickFact>
          );
        })()}
      <SeoLocationFaqList
        titleCasedServiceKeyword={titleCasedServiceKeyword}
        locationName={locationName}
      />

      <SeoLocationText my="12x" text={details.text} />
      {!isCatInAFlat && (
        <Section mt={['8x', '16x']}>
          <AppStoreCallout />
        </Section>
      )}
      <SeoLocationFooterLinks
        breadcrumbData={breadcrumbData}
        serviceName={sentenceCasedServiceKeyword}
        locationName={cityName}
        linkedLocations={linkedLocations}
        topNeighborhoodServices={topNeighborhoodServices}
        relatedServices={relatedServices}
        internalServiceTypeSlug={internalServiceType.slug}
      />
    </SeoLocationPageWrapper>
  );
};

export default SeoLocationPageTemplate;
